import React, { useEffect, useMemo } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Select from "components/ui/Select";
import useModalStore from "store/modals";
import { useAllItems } from "http-hooks/item/useItem";

const SelectRawItems = ({
  value,
  onChange,
  placeholder = "Search items...",
  isDisabled = false,
  isRequired = false,
  label = "Raw Item",
  className = "",
  filters = {},
  showLabel = true,
  showAddButton = true, // New prop to control plus button visibility
}) => {
  const { openModal } = useModalStore();
  
  const {
    isLoading: isLoadingAllItems,
    data: { data: allItems } = [],
  } = useAllItems({
    item_name: "",
    item_code: "",
    ag_status: "1",
    noPagination: true,
    ...filters,
  });

  // Convert items to options for Select component
  const itemOptions = useMemo(() => {
    return allItems?.map(item => ({
      value: item.item_id,
      label: `${item.item_code} ${item.item_name}`.trim(),
      item_name: item.item_name,
      item_code: item.item_code,
      ...item
    })) || [];
  }, [allItems]);

  // Auto-select the matching option when items load and value is provided as simple object
  useEffect(() => {
    // If value is provided as a simple object with item_code and item_name but not as a full option
    if (value && !value.value && value.item_name && itemOptions.length > 0 && onChange) {
      const matchedOption = itemOptions.find(option => {
        // Match by item_name (required)
        const nameMatch = String(option.item_name) === String(value.item_name);
        
        // Normalize empty values: treat null, undefined, empty string, and whitespace as the same
        const normalizeCode = (code) => {
          if (!code || String(code).trim() === "") return "";
          return String(code).trim();
        };
        
        const normalizedValueCode = normalizeCode(value.item_code);
        const normalizedOptionCode = normalizeCode(option.item_code);
        
        // Match codes: both empty OR both equal
        const codeMatch = normalizedValueCode === normalizedOptionCode;
        
        return nameMatch && codeMatch;
      });
      
      if (matchedOption && matchedOption !== value) {
        onChange(matchedOption);
      }
    }
  }, [value, itemOptions, onChange]);

  const handleAddNew = () => {
    openModal('item');
  };

  return (
    <div>
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          {showAddButton && !isDisabled && (
            <button
              type="button"
              onClick={handleAddNew}
              className="ml-2 p-1 text-xs font-bold bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200 flex items-center"
              title="Add new item"
            >
              <PlusIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <Select
        value={value}
        onChange={onChange}
        options={itemOptions}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={isLoadingAllItems}
        isRequired={isRequired}
        label={label}
        className={className}
        showLabel={false}
        isClearable={true}
        isSearchable={true}
      />
      {!showLabel && showAddButton && !isDisabled && (
        <button
          type="button"
          onClick={handleAddNew}
          className="absolute top-0 right-0 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
          title="Add new item"
        >
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default SelectRawItems; 